import React, { FC, ReactNode, useContext, useState } from 'react'

import { useLocalStorage } from 'react-use'

// Constants
import { localStorageKeys } from '@sketch/constants'

// Utils
import { useFlag } from 'containers/flags'
import { useCanUseDesignSystemManager } from 'modules/designSystems/operations/useCanUseDesignSystemManager'
import { useIsProfileEnabled } from 'modules/workspaceProfile/hooks/useIsProfileEnabled'
import {
  canUserAdministerShares as getCanUserAdministerShares,
  showWorkspaceSettings as getShowWorkspaceSettings,
} from 'modules/workspace/utils'

// Components
import * as Collapsible from '@radix-ui/react-collapsible'
import { Box } from '@sketch/components'
import UserNavbarSection from 'modules/user/components/UserNavbarSection'
import { EmptyTrashModal } from 'modules/shares/components/EmptyTrashModal'
import { ModalContext } from 'containers/ModalManager'
import { ProjectsMenu } from 'modules/projects/components/ProjectsMenu'
import { useShareDeleteWithUi } from 'modules/shares/components/DeleteShareModal'
import { DesignSystemSidebar } from 'modules/designSystems/components'

// Local Components
import { WorkspacesDropdown } from '../../containers/WorkspacesDropdown/WorkspacesDropdown'
import { MenuItem, getMenuItems, getWorkspaceMenuItems } from './menuItems'
import BottomMenu from './components/BottomMenu/BottomMenu'

// Styles
import {
  SidebarWrapper,
  WorkspaceSelectorWrapper,
  MenuWrapper,
  Header,
  FooterWrapper,
  CollapsibleIcon,
} from './Sidebar.styles'

// GQL
import {
  GetProjectsQuery,
  useGetInitialUserQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

interface SidebarProps {
  workspace: WorkspaceMinimalFragment
}

// Helper Components
const renderMenuItems = (menuItems: MenuItem[]): ReactNode =>
  menuItems.map(({ key, item, hidden }) =>
    !hidden ? <React.Fragment key={key}>{item}</React.Fragment> : null
  )

export const Sidebar: FC<SidebarProps> = ({ workspace }) => {
  const { showModal } = useContext(ModalContext)

  /**
   * Sidebar FF clean up
   * https://github.com/sketch-hq/Cloud/issues/15634
   */
  const isSidebarFFOn = useFlag('sidebar')
  const hasWorkspaceTemplatesEnabled = useFlag('set-as-template')
  const hasGuestsInProjects = useFlag('guests-in-project')
  const isDesignSystemsOn = useFlag('design-systems')

  const { isProfileEnabled } = useIsProfileEnabled(workspace.identifier)

  const { data: userData } = useGetInitialUserQuery()

  const { canUseDesignSystemManager } = useCanUseDesignSystemManager({
    workspace,
  })

  const localStorageKey = localStorageKeys.sidebarSectionDocumentsCollapsed
  const [
    isCollapsedDocumentsSection,
    setIsCollapsedDocumentsSection,
  ] = useLocalStorage(localStorageKey)

  const canUserAdministerShares = getCanUserAdministerShares(workspace)
  const showWorkspaceSettings = getShowWorkspaceSettings(workspace)

  const showUnseenNotificationBadge =
    userData?.me.hasUnseenNotifications ?? false

  //   Projects
  const isFinance = workspace.userRole === 'FINANCE'
  const isGuest = workspace.userRole === 'GUEST'
  const isPartner = userData?.me.isPartner
  const isWorkspaceActive = workspace.status === 'ACTIVE'

  // if Sidebar FF is ON, we show the projects else, we check the workspace status
  const canShowProjectsForInactiveWorkspaces =
    isSidebarFFOn || isWorkspaceActive

  const showProjectsList =
    userData &&
    canShowProjectsForInactiveWorkspaces &&
    !isFinance &&
    !isPartner &&
    // only show the projects list for guests with the `guests-in-project`
    // feature flag enabled
    (!isGuest || hasGuestsInProjects)

  const [hasDraftsProject, setHasDraftsProject] = useState(false)

  const handleProjectsLoad = (data: GetProjectsQuery) => {
    const draftsProject = data?.workspace.draftsProject.entries[0]
    setHasDraftsProject(Boolean(draftsProject))
  }

  // Delete share
  const [deleteShare] = useShareDeleteWithUi()

  const handleOnEmptyTrash = () => {
    showModal(EmptyTrashModal, {
      workspaceId: workspace.identifier,
    })
  }

  // Items
  const menuItems = getMenuItems({
    workspaceId: workspace.identifier,
    showWorkspaceSettings: !isSidebarFFOn && showWorkspaceSettings,
    showUnseenNotificationBadge,
    showUpdatesLink: !isSidebarFFOn,
    showHelpLink: !isSidebarFFOn,
    showDiscoverLink: !isSidebarFFOn,
    userRole: workspace.userRole,
  })

  const workspaceMenuItems = getWorkspaceMenuItems({
    workspaceId: workspace.identifier,
    canUseDesignSystemManager,
    userRole: workspace.userRole,
    hasDraftsProject,
    hasWorkspaceTemplatesEnabled,
    canUserAdministerShares,
    handleOnEmptyTrash,
    deleteShare,
    isProfileEnabled,
  })

  const handleCollapsibleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsCollapsedDocumentsSection(!isCollapsedDocumentsSection)
  }

  return (
    <SidebarWrapper>
      {workspace && (
        <WorkspaceSelectorWrapper>
          <WorkspacesDropdown workspace={workspace} />
        </WorkspaceSelectorWrapper>
      )}
      <MenuWrapper>
        {renderMenuItems(menuItems)}

        {!isPartner && !isSidebarFFOn && (
          <>
            <Header>Workspace</Header>
            {renderMenuItems(workspaceMenuItems)}
          </>
        )}

        {!isPartner && isSidebarFFOn && (
          <>
            <Collapsible.Root
              data-testid="documents"
              className="CollapsibleRoot"
              open={!isCollapsedDocumentsSection}
              onOpenChange={
                setIsCollapsedDocumentsSection as (isOpen: boolean) => void
              }
            >
              {/** * Sidebar FF clean up *
              https://github.com/sketch-hq/Cloud/issues/15634 */}
              <Header isSidebarFFOn>
                Documents
                <Collapsible.Trigger
                  data-testid="collapse-documents-button"
                  asChild
                >
                  <CollapsibleIcon
                    isOpen={!isCollapsedDocumentsSection}
                    onClick={handleCollapsibleClick}
                  />
                </Collapsible.Trigger>
              </Header>
              {!isCollapsedDocumentsSection ? (
                <Collapsible.Content data-testid="documents-content">
                  {renderMenuItems(workspaceMenuItems)}
                </Collapsible.Content>
              ) : (
                <>
                  {renderMenuItems(
                    workspaceMenuItems.filter((item: MenuItem) => item.isActive)
                  )}
                </>
              )}
            </Collapsible.Root>
          </>
        )}

        {(!isDesignSystemsOn || !canUseDesignSystemManager) && <Box mb={4} />}

        {isDesignSystemsOn && canUseDesignSystemManager && (
          <DesignSystemSidebar workspace={workspace} />
        )}

        {showProjectsList && (
          <ProjectsMenu
            workspace={workspace}
            canUserAdministerShares={canUserAdministerShares}
            onLoadCompleted={handleProjectsLoad}
          />
        )}

        {isDesignSystemsOn && !canUseDesignSystemManager && (
          <DesignSystemSidebar workspace={workspace} />
        )}
      </MenuWrapper>
      <FooterWrapper>
        {isSidebarFFOn && <BottomMenu workspace={workspace} />}
        <UserNavbarSection />
      </FooterWrapper>
    </SidebarWrapper>
  )
}
